import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAdminApps } from '@/admin/useAdminApps';
import { useAdminRoles } from '@/admin/useAdminRoles';
import { useSession } from '@/auth/useSession';
import { AppsTable } from '@/components/admin/AppsTable';
import { CreateRoleForm } from '@/components/admin/CreateRoleForm';
import { RolesTable } from '@/components/admin/RolesTable';

type Tab = 'roles' | 'apps';

export function Admin() {
  const { t } = useTranslation();
  const session = useSession();
  // Roles es la gestión principal desde el portal (las apps se aprovisionan por
  // código), así que abre en esa pestaña por defecto.
  const [tab, setTab] = useState<Tab>('roles');

  if (session.isLoading) {
    return <p className="text-slate-600">…</p>;
  }
  if (session.isError || !session.data) {
    return <Navigate to="/login" replace />;
  }
  if (!session.data.is_admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <section aria-labelledby="admin-title" className="space-y-6">
      <header>
        <h2 id="admin-title" className="text-2xl font-semibold text-appsol-900">
          {t('admin.title')}
        </h2>
        <p className="mt-1 text-sm text-slate-600">{t('admin.subtitle')}</p>
      </header>

      <div role="tablist" aria-label={t('admin.tabs.label')} className="flex gap-1 border-b">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'roles'}
          onClick={() => setTab('roles')}
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
            tab === 'roles'
              ? 'border-appsol-500 text-appsol-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          {t('admin.tabs.roles')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'apps'}
          onClick={() => setTab('apps')}
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
            tab === 'apps'
              ? 'border-appsol-500 text-appsol-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          {t('admin.tabs.apps')}
        </button>
      </div>

      {tab === 'apps' ? <AppsSection /> : <RolesSection />}
    </section>
  );
}

function AppsSection() {
  const { t } = useTranslation();
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const apps = useAdminApps(includeDeleted);

  return (
    <div className="space-y-6">
      <p className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
        {t('admin.managedByCode')}
      </p>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          />
          {t('admin.includeDeleted')}
        </label>
      </div>

      {apps.isLoading && <p className="text-sm text-slate-500">{t('home.loading')}</p>}
      {apps.isError && (
        <p role="alert" className="text-sm text-rose-700">
          {t('admin.errors.listFailed')}
        </p>
      )}
      {apps.data && <AppsTable apps={apps.data} />}
    </div>
  );
}

function RolesSection() {
  const { t } = useTranslation();
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const roles = useAdminRoles(includeDeleted);

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600">{t('roles.subtitle')}</p>

      <CreateRoleForm />

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          />
          {t('roles.includeDeleted')}
        </label>
      </div>

      {roles.isLoading && <p className="text-sm text-slate-500">{t('home.loading')}</p>}
      {roles.isError && (
        <p role="alert" className="text-sm text-rose-700">
          {t('roles.errors.listFailed')}
        </p>
      )}
      {roles.data && <RolesTable roles={roles.data} />}
    </div>
  );
}
